/**
 * Worker Dashboard — single aggregate view (NEXT_SESSION P0.2).
 *
 * Powered by ``GET /v1/me/dashboard`` which returns the entire
 * surface in one round-trip:
 *
 *   today           clock-in status + scheduled times + rest/holiday
 *   this_week       Mon..Sun by-day hours + week total vs scheduled
 *   month_summary   completed / late / absent / overtime aggregates
 *   leave_balances  per-type remaining + total + used
 *   pending         tasks / requests / approvals counts
 *   recent_announcements
 *   upcoming_events
 *
 * Mobile (RN/Flutter) reuses the same endpoint so the home screen
 * is one fetch on cold start. This file is the web rendering of
 * that contract.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NAV_ICON } from './workerNavIcons';
import {
  Box, Typography, Paper, CircularProgress, Chip, Stack, Button,
  LinearProgress, Divider,
} from '@mui/material';
import {
  AccessTimeOutlined, EventNoteOutlined, AssignmentOutlined, BeachAccessOutlined, CampaignOutlined,
  CheckCircleOutlined, WarningAmberOutlined,
  FaceOutlined, ArrowForwardOutlined,
  FreeBreakfastOutlined,
  PhoneIphoneOutlined,
  HistoryOutlined,
  CalendarMonthOutlined,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../../auth/AuthContext';
import { meApi } from '../../api';
import type { DashboardResponse, MeProfile, MePunch } from '../../api/me';
import { activeLocale, fmtLocalTime } from '../../utils/datetime';
import { deriveBreakSegments, breakSummary } from '../../utils/breaks';
import GetAppCard from '../../components/face/GetAppCard';
import { STORE_APPS } from '../../components/face/appStore';
import { hasProduct } from '../../auth/productAccess';

const WorkerDashboard: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<DashboardResponse | null>(null);
  // Issue 4 setup-banner (Path C 2026-05-22) — drive a dashboard
  // banner from face consent state. Fetched in parallel with the
  // dashboard payload; best-effort .catch fallback so a profile
  // 4xx/5xx doesn't black out the home screen.
  const [profile, setProfile] = useState<MeProfile | null>(null);
  // ADR-033 — today's break segments (web mirror of the mobile Today card).
  const [breakText, setBreakText] = useState('');
  // Today's punch detail (migrated from the removed /me/clock page — web is
  // view-only; punching is mobile-app only as of 2026-06-30).
  const [punchRows, setPunchRows] = useState<MePunch[]>([]);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const [d, p] = await Promise.all([
        meApi.getDashboard(),
        meApi.getProfile().catch(() => null),
      ]);
      setData(d);
      setProfile(p);
    } catch { /* leave data null → empty fallback */ }
    finally { setLoading(false); }
    // Best-effort: derive today's break range from the punch stream.
    try {
      const today = new Date().toISOString().slice(0, 10);
      const rows = await meApi.punches(today);
      setPunchRows(rows as MePunch[]);
      setBreakText(breakSummary(deriveBreakSegments(rows as MePunch[]), (time) => t('worker:clock.breakFrom', { time })));
    } catch { setBreakText(''); }
  }, [t]);

  useEffect(() => { fetch(); }, [fetch]);

  // Live clock for the hero date/time module (owner 2026-06-26: 让用户第一眼
  //就知道今天/现在). Ticks once per second; mirrors the mobile dashboard hero.
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const firstName = (user?.full_name || user?.email?.split('@')[0] || t('worker:dashboard.greetingFallback', { defaultValue: 'there' })).split(' ')[0];
  /**
   * THE PAGE'S LICENCE, read once (owner, production, 2026-09-09).
   *
   * THE DEFECT: this page had no licence gate at all. A PureOA-only worker was shown a `Today` card
   * saying "Not clocked in", a `This week` card reading `0.0h / 48h scheduled`, a QuTime download QR with
   * App Store and Play buttons, `Today's entries` ("No punches yet today"), a month card of
   * Completed / Late / Absent / Overtime, a leave balance, and a header line reading
   * `Scheduled 09:00 → 18:30` — none of which their organization has bought.
   *
   * The tell was in the same screenshot: the SIDEBAR was correctly gated and showed only PureOA items.
   * Someone gated the navigation and never gated the page it lands on, so eight attendance surfaces
   * rendered EMPTY rather than being absent — which is the one thing svdy-mobile-ux §12 names as the
   * cardinal sin ("remove, don't empty"), because a grid of blank attendance cards reads as a broken
   * product rather than as a product you do not own.
   *
   * `hasProduct` is FAIL-CLOSED: no resolved licence means no answer, so nothing attendance-shaped
   * appears while the payload is in flight. The same helper and the same payload field already gated
   * `faceEnabled` two lines down — the primitive was here all along.
   */
  const licence = { products: data?.licensed_products ?? [], resolved: data !== null };
  const hasQutime = hasProduct(licence, 'qutime');
  const hasPureoa = hasProduct(licence, 'pureoa');
  // Face capture is QuTime-only. Read from the dashboard payload already in hand
  // (`licensed_products`), fail-closed while it is null: no answer, no prompt.
  const faceEnabled = hasQutime;
  const hour = new Date().getHours();
  const greeting = hour < 12 ? t('worker:dashboard.goodMorning', { defaultValue: 'Good morning' })
    : hour < 18 ? t('worker:dashboard.goodAfternoon', { defaultValue: 'Good afternoon' })
    : t('worker:dashboard.goodEvening', { defaultValue: 'Good evening' });

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress size={28} /></Box>;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: '12px', width: '100%' }}>
      {/* Greeting + date */}
      <Box sx={{ mb: 3, width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem' }}>
            {greeting}, {firstName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {now.toLocaleDateString(i18n.language, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            {/* SCHEDULE, REST DAY and PUBLIC HOLIDAY are attendance facts, so they follow the licence.
                The existing `scheduled_in && scheduled_out` guard is a DATA check and it does not stand
                in for a licence check: the endpoint returns a timetable for a PureOA-only worker too, so
                a worker whose org never bought attendance was being told
                `Scheduled 09:00 → 18:30 (Standard Timetable)`.

                The live clock beside this is deliberately NOT gated — it is the date/time module the
                owner asked for in 2026-06-26, useful on any dashboard, and not a punch control. */}
            {hasQutime && data?.today.scheduled_in && data?.today.scheduled_out && (
              <>
                {` · ${t('worker:dashboard.scheduledLabel')} `}
                <strong>{data.today.scheduled_in} → {data.today.scheduled_out}</strong>
                {data.today.timetable_name ? ` (${data.today.timetable_name})` : ''}
            </>
          )}
          {hasQutime && data?.today.is_rest_day && ` · ${t('worker:dashboard.restDayInline')}`}
          {hasQutime && data?.today.is_holiday && ` · ${t('worker:dashboard.publicHolidayInline')}`}
        </Typography>
        </Box>
        {/* Live clock — the date/time module (owner 2026-06-26). */}
        <Typography
          variant="h4"
          sx={{ fontWeight: 300, fontFamily: 'monospace', letterSpacing: '-0.02em', color: 'text.primary', lineHeight: 1 }}
        >
          {now.toLocaleTimeString(activeLocale(), { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}
        </Typography>
      </Box>

      {/* Issue 4 setup-banner (Path C 2026-05-22) — surfaces only
          when the worker hasn't enrolled yet. Disappears the moment
          consent is recorded (next dashboard refresh). The Account →
          Face matching sidebar entry is the always-on path; this
          banner is the contextual nudge for the "first login, hasn't
          set up" use case.

          QuTime-only, fail-closed (2026-08-14). "Has not enrolled yet" is true of
          every worker in a PureOA-only org and always will be, so without the
          licence check this banner is permanent — the loudest element on the home
          screen, asking for a face scan the org never uses. The licence comes from
          the SAME dashboard payload this page already fetched, so it costs no
          request; `data === null` (the fetch failed) reads as unlicensed. */}
      {faceEnabled && profile && !profile.face_template_consented_at && (
        <Paper
          elevation={0}
          role="link"
          tabIndex={0}
          onClick={() => navigate('/me/face-template')}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              navigate('/me/face-template');
            }
          }}
          sx={{
            width: '100%',
            mb: 1.5,
            p: 1.5,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'warning.light',
            bgcolor: 'rgba(237,108,2,0.06)',
            cursor: 'pointer',
            transition: 'background-color 120ms',
            '&:hover': { bgcolor: 'rgba(237,108,2,0.10)' },
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <FaceOutlined sx={{ color: 'warning.main', fontSize: 22, flexShrink: 0 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 700, fontSize: '0.875rem' }}
              >
                {t('worker:dashboard.setupFace')}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                {t('worker:dashboard.setupFaceHint')}
              </Typography>
            </Box>
            <Button
              size="small"
              variant="contained"
              endIcon={<ArrowForwardOutlined sx={{ fontSize: 14 }} />}
              onClick={(e) => {
                e.stopPropagation();
                navigate('/me/face-template');
              }}
              sx={{
                textTransform: 'none',
                borderRadius: 1,
                fontSize: '0.8125rem',
                flexShrink: 0,
              }}
            >
              {t('worker:dashboard.setUp')}
            </Button>
          </Stack>
        </Paper>
      )}

      {/* Hero row: today + week + pending */}
      {/* CSS Grid (svdy-ui §11.1) — avoids MUI Grid container negative-margin
          drift when sibling rows share the parent <Box>. */}
      {/* The COLUMN COUNT follows the licence, not a constant: with the two attendance cards absent, a
          `repeat(3, 1fr)` grid would leave `Pending` in the first third of the row with two empty tracks
          beside it — the ragged-edge failure §11.4 is about, arrived at from the other direction. */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: hasQutime ? 'repeat(3, 1fr)' : '1fr' },
        gap: 1.5,
        width: '100%',
        mb: 1.5,
      }}>
        {hasQutime && (
          <TodayCard data={data} breakText={breakText} onAction={() => navigate('/me/time-entries')} />
        )}
        {hasQutime && <WeekCard data={data} />}
        <PendingCard data={data} navigate={navigate} hasQutime={hasQutime} hasPureoa={hasPureoa} />
      </Box>

      {/* Punching is mobile-app only (web has no reliable geofence on desktop) —
          a clear reminder + the migrated today's-entries detail.

          QuTime-ONLY, and this row is the sharpest case: `ClockOnAppCard` shows a QR code and App Store /
          Play buttons for the QuTime app. Advertising a product the organization has not bought is worse
          than an empty card. */}
      {hasQutime && (
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
          gap: 1.5,
          width: '100%',
          mb: 1.5,
        }}>
          <ClockOnAppCard />
          <TodayEntriesCard rows={punchRows} onViewAll={() => navigate('/me/time-entries')} />
        </Box>
      )}

      {/* PureOA-only organizations install the PureOA app (a bundle organization uses the QuTime app,
          which carries PureOA too). Rendered only once the PureOA listing is public: a worker gains
          nothing from a "coming soon" card, and `STORE_APPS.pureoa.live` flips it on with no edit here. */}
      {hasPureoa && !hasQutime && STORE_APPS.pureoa.live && (
        <Paper elevation={0} sx={{ ...cardSx, width: '100%', mb: 1.5 }}>
          <GetAppCard compact brands={['pureoa']} subtitle={t('worker:getApp.pureoaSubtitle')} />
        </Paper>
      )}

      {/* Month + Leave row — same 1:1 split as the Announcements +
          Events row below, so the right edges of the two rows
          align (svdy-ui §11.1 + §11.4 ragged-edge avoidance). */}
      {hasQutime && (
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
          gap: 1.5,
          width: '100%',
          mb: 1.5,
        }}>
          <MonthCard data={data} navigate={navigate} />
          <LeaveCard data={data} navigate={navigate} />
        </Box>
      )}

      {/* Announcements + Events */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
        gap: 1.5,
        width: '100%',
      }}>
        <FeedCard
          title={t('worker:dashboard.recentAnnouncements')}
          icon={<CampaignOutlined sx={{ fontSize: 18, color: 'primary.main' }} />}
          items={(data?.recent_announcements ?? []).map(a => ({
            id: a.id,
            title: a.title,
            subtitle: a.published_at ? new Date(a.published_at).toLocaleDateString(activeLocale()) : '',
          }))}
          emptyText={t('worker:dashboard.noAnnouncements')}
          onItemClick={() => navigate('/me/schedule')}
        />
        <FeedCard
          title={t('worker:dashboard.upcomingEvents')}
          icon={<EventNoteOutlined sx={{ fontSize: 18, color: 'info.main' }} />}
          items={(data?.upcoming_events ?? []).map(e => ({
            id: e.id,
            title: e.title,
            subtitle: e.event_date ? new Date(e.event_date).toLocaleDateString(activeLocale()) : '',
          }))}
          emptyText={t('worker:dashboard.noEvents')}
          onItemClick={() => navigate('/me/schedule')}
        />
      </Box>
    </Box>
  );
};


// ===========================================================================
// Subcards
// ===========================================================================

const cardSx = {
  p: 2,
  borderRadius: 2,
  border: '1px solid',
  borderColor: 'divider',
  height: '100%',
};

/**
 * Card shell for the two list cards (today's punches, announcements/events).
 *
 * Grid rows stretch every card to the tallest sibling, so a list card with
 * nothing in it used to render its one-line empty text pinned under the header
 * with a large void beneath — "Today's entries / No punches yet today." sitting
 * in a card three times taller than its content (owner report 2026-07-28). The
 * body is a flex child here, so the empty state can occupy that space and sit
 * centred in it instead of leaving a hole.
 *
 * The header is the same shape as every other card on this dashboard: leading
 * icon, bold title, optional action on the right. `Today's entries` was the only
 * card without an icon, which broke the row's first-content baseline
 * (svdy-ui §11.3).
 */
const listCardSx = {
  ...cardSx,
  display: 'flex',
  flexDirection: 'column' as const,
};

const CardHeader: React.FC<{
  icon: React.ReactNode;
  title: string;
  action?: React.ReactNode;
}> = ({ icon, title, action }) => (
  <Stack
    direction="row"
    alignItems="center"
    justifyContent="space-between"
    sx={{ mb: 1, minHeight: 28 }}
  >
    <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
      {icon}
      <Typography variant="body2" sx={{ fontWeight: 600 }}>{title}</Typography>
    </Stack>
    {action}
  </Stack>
);

/**
 * Empty state for a list card.
 *
 * Left-aligned on purpose. Centring the text (the first version of this, 2026-07-28)
 * made an empty card the only element on the page whose content did not start at
 * the card's inner edge, so `Recent announcements` (a left-aligned list) and
 * `Upcoming events` (centred placeholder) read as two unrelated cards sitting in
 * the same row. A page looks composed when every card shares ONE alignment axis;
 * filling vertical space is not worth breaking it.
 *
 * Vertical centring stays: grid rows stretch cards to the tallest sibling, and a
 * single line pinned under the header with a tall void beneath is what the owner
 * flagged in the first place. Left axis, vertically settled.
 */
const CardEmptyState: React.FC<{ text: string }> = ({ text }) => (
  <Box
    sx={{
      flex: 1,
      minHeight: 56,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
    }}
  >
    <Typography variant="caption" color="text.disabled">{text}</Typography>
  </Box>
);

const TodayCard: React.FC<{ data: DashboardResponse | null; breakText?: string; onAction: () => void }> = ({ data, breakText, onAction }) => {
  const { t } = useTranslation();
  const te = data?.today.time_entry;
  const clockedIn = !!te?.clock_in;
  const clockedOut = !!te?.clock_out;
  // Timezone fix (2026-06-26): convert UTC ISO → browser-local; '' so the
  // `&&` render guards below stay correct when there's no punch yet.
  const inTime = fmtLocalTime(te?.clock_in, '');
  const outTime = fmtLocalTime(te?.clock_out, '');
  return (
    <Paper elevation={0} sx={cardSx}>
      <CardHeader
        icon={<AccessTimeOutlined sx={{ fontSize: 18, color: 'primary.main' }} />}
        title={t('worker:dashboard.today')}
      />
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
        {clockedIn ? (
          <CheckCircleOutlined sx={{ fontSize: 16, color: 'success.main' }} />
        ) : (
          <WarningAmberOutlined sx={{ fontSize: 16, color: 'text.disabled' }} />
        )}
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {clockedOut ? t('worker:dashboard.dayComplete') : clockedIn ? t('worker:dashboard.clockedIn') : t('worker:dashboard.notClockedIn')}
        </Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        {inTime && <>{t('worker:dashboard.in')} <strong>{inTime}</strong></>}
        {outTime && <> · {t('worker:dashboard.out')} <strong>{outTime}</strong></>}
        {te?.total_hours != null && <> · <strong>{Number(te.total_hours).toFixed(1)}h</strong></>}
        {!clockedIn && data?.today.is_rest_day && t('worker:dashboard.restDayNote')}
        {!clockedIn && !data?.today.is_rest_day && (data?.today.scheduled_in
          ? t('worker:dashboard.shiftStartsAt', { time: data.today.scheduled_in })
          : t('worker:dashboard.noScheduledShift'))}
      </Typography>
      {breakText ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
          <FreeBreakfastOutlined sx={{ fontSize: 13 }} />
          {t('worker:clock.breakLabel')} · {breakText}
        </Typography>
      ) : null}
      <Button
        size="small"
        variant="outlined"
        onClick={onAction}
        sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.8125rem' }}
      >
        {t('worker:dashboard.viewDetails')}
      </Button>
    </Paper>
  );
};

// Punching moved to the mobile app (2026-06-30) — desktop browsers can't
// geofence reliably, so the web portal is view-only and points workers to the
// app for face-verified clock in/out.
const ClockOnAppCard: React.FC = () => {
  const { t } = useTranslation();
  return (
    <Paper elevation={0} sx={cardSx}>
      <CardHeader
        icon={<PhoneIphoneOutlined sx={{ fontSize: 18, color: 'primary.main' }} />}
        title={t('worker:dashboard.clockOnApp.title')}
      />
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        {t('worker:dashboard.clockOnApp.body')}
      </Typography>
      <GetAppCard compact brands={['qutime']} />
    </Paper>
  );
};

const TodayEntriesCard: React.FC<{ rows: MePunch[]; onViewAll: () => void }> = ({ rows, onViewAll }) => {
  const { t } = useTranslation();
  return (
    <Paper elevation={0} sx={listCardSx}>
      <CardHeader
        icon={<HistoryOutlined sx={{ fontSize: 18, color: 'primary.main' }} />}
        title={t('worker:clock.punchesToday')}
        action={
          <Button size="small" onClick={onViewAll} sx={{ textTransform: 'none', fontSize: '0.75rem', minWidth: 0 }}>
            {t('worker:clock.viewAllEntries')}
          </Button>
        }
      />
      {rows.length === 0 ? (
        <CardEmptyState text={t('worker:dashboard.noEntriesToday')} />
      ) : (
        <Stack spacing={0.75}>
          {rows.map((p) => {
            const tm = fmtLocalTime(p.punched_at);
            const isBreak = p.punch_type === 'break_out' || p.punch_type === 'break_in';
            return (
              <Stack key={p.id} direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, minWidth: 52 }}>{tm}</Typography>
                {isBreak && (
                  <Chip icon={<FreeBreakfastOutlined sx={{ fontSize: 12 }} />} label={t(p.punch_type === 'break_out' ? 'worker:clock.breakOut' : 'worker:clock.breakIn')} size="small" variant="outlined" sx={{ borderRadius: 1, height: 20, fontSize: '0.65rem' }} />
                )}
                {p.is_valid ? (
                  <Chip label={t('worker:clock.recorded')} size="small" color="success" variant="outlined" sx={{ borderRadius: 1, height: 20, fontSize: '0.65rem' }} />
                ) : (
                  <Chip label={t(`worker:clock.punchReason.${p.invalid_reason ?? 'invalid'}`, { defaultValue: t('worker:clock.recorded') })} size="small" variant="outlined" sx={{ borderRadius: 1, height: 20, fontSize: '0.65rem', opacity: 0.7 }} />
                )}
                {p.within_geofence != null && (
                  <Chip label={p.within_geofence ? t('worker:clock.insideWorkArea') : t('worker:clock.outsideWorkArea')} size="small" color={p.within_geofence ? 'success' : 'warning'} variant="outlined" sx={{ borderRadius: 1, height: 20, fontSize: '0.65rem' }} />
                )}
              </Stack>
            );
          })}
        </Stack>
      )}
    </Paper>
  );
};

const WeekCard: React.FC<{ data: DashboardResponse | null }> = ({ data }) => {
  const { t } = useTranslation();
  const wk = data?.this_week;
  const pct = wk && wk.scheduled_hours > 0
    ? Math.min(100, (wk.actual_hours / wk.scheduled_hours) * 100)
    : 0;
  return (
    <Paper elevation={0} sx={cardSx}>
      <CardHeader
        icon={<EventNoteOutlined sx={{ fontSize: 18, color: 'info.main' }} />}
        title={t('worker:dashboard.thisWeek')}
      />
      <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 1.25 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1 }}>
          {wk?.actual_hours.toFixed(1) ?? '0.0'}h
        </Typography>
        <Typography variant="caption" color="text.secondary">
          / {wk?.scheduled_hours.toFixed(0) ?? '0'}h scheduled
        </Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{ height: 4, borderRadius: 2, bgcolor: 'grey.100', mb: 1.5 }}
      />
      {/* By-day strip */}
      <Stack direction="row" justifyContent="space-between">
        {(wk?.by_day ?? []).map((d, i) => {
          const tone = d.status === 'late' ? 'warning.main'
            : d.status === 'absent' ? 'error.main'
            : d.hours > 0 ? 'success.main' : 'grey.300';
          return (
            <Box key={i} sx={{ textAlign: 'center', minWidth: 28 }}>
              <Typography variant="caption" sx={{ fontSize: '0.6875rem', color: 'text.secondary', display: 'block' }}>
                {['M','T','W','T','F','S','S'][i]}
              </Typography>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: tone, mx: 'auto', mt: 0.5 }} />
            </Box>
          );
        })}
      </Stack>
    </Paper>
  );
};

const PendingCard: React.FC<{
  data: DashboardResponse | null;
  navigate: (to: string) => void;
  /** PASSED IN, not re-derived (2026-09-09). This card read `data.licensed_products` itself with a bare
   *  `.includes()`, so the page had two answers to "which products" — one fail-closed via `hasProduct`,
   *  one not — and only the parent's was audited when the page was found to have no licence gate at all.
   *  One derivation per page (svdy-rules §10.5b). */
  hasQutime: boolean;
  hasPureoa: boolean;
}> = ({ data, navigate, hasQutime, hasPureoa }) => {
  const { t } = useTranslation();
  const p = data?.pending;

  /**
   * ONE ROW PER SURFACE, and each row counts exactly what its destination shows.
   *
   * Owner, production, 2026-09-05: the Requests row read `3`, the click landed on the process list, and
   * the process list read `0`. The three were ATTENDANCE requests.
   *
   * The count was never wrong. `pending.requests` is a SUM — process requests I submitted PLUS my own
   * pending attendance requests — and that was correct while the portal had ONE merged Requests entry a
   * summed count could link to. Splitting the hubs into per-surface pages (option B, 2026-09-04) left
   * the card linking the sum to one component. `pending.approvals` had the identical defect and was
   * invisible only because all three of its parts were zero.
   *
   * So the invariant is explicit here: a row that shows a count and navigates somewhere must count
   * exactly what the destination shows. The server already had every component and was only adding
   * them up; `DashboardPendingBlock` now emits them.
   *
   * The two visibility rules differ, and the difference is not arbitrary:
   *   MY OWN WORK (tasks, requests)  shown on LICENCE, including at zero — the sidebar carries the
   *                                 entry regardless, and a zero row is a place the worker can go.
   *   REVIEWER QUEUES (approvals)    shown only when the count is non-zero. Those sidebar entries are
   *                                 `canReview`-gated and this endpoint does not report `canReview`,
   *                                 but the counts come from `_reviewer_pending_counts`, so a
   *                                 non-reviewer is always zero. A non-zero count IS the permission,
   *                                 and it avoids offering a queue the API would refuse.
   *
   * Labels reuse the PAGE TITLES rather than card-local strings, so the card cannot drift from the
   * sidebar and the page it opens — one name per surface.
   */
  const rows: { key: string; label: string; count: number; path: string }[] = [];
  if (hasPureoa) {
    rows.push({ key: 'tasks', label: t('worker:tasks.title'), count: p?.tasks ?? 0, path: '/me/tasks' });
  }
  if (hasQutime) {
    rows.push({
      key: 'attendance-requests',
      label: t('worker:attendanceRequests.title'),
      count: p?.attendance_requests ?? 0,
      path: '/me/attendance-requests',
    });
  }
  if (hasPureoa) {
    rows.push({
      key: 'requests',
      label: t('worker:requests.title'),
      count: p?.process_requests ?? 0,
      path: '/me/requests',
    });
  }
  if ((p?.process_approvals ?? 0) > 0) {
    rows.push({
      key: 'approvals',
      label: t('worker:approvals.title'),
      count: p?.process_approvals ?? 0,
      path: '/me/approvals',
    });
  }
  if ((p?.attendance_approvals ?? 0) > 0) {
    rows.push({
      key: 'attendance-approvals',
      label: t('worker:attendanceApprovals.title'),
      count: p?.attendance_approvals ?? 0,
      path: '/me/attendance-approvals',
    });
  }
  if ((p?.face_approvals ?? 0) > 0) {
    rows.push({
      key: 'face-reviews',
      label: t('worker:faceReviews.title'),
      count: p?.face_approvals ?? 0,
      path: '/me/face-reviews',
    });
  }

  return (
    <Paper elevation={0} sx={cardSx}>
      <CardHeader
        icon={<AssignmentOutlined sx={{ fontSize: 18, color: 'warning.main' }} />}
        title={t('worker:dashboard.pending')}
      />
      <Stack spacing={1}>
        {rows.length === 0 ? (
          // An unlicensed shell, or entitlement still loading. Says so rather than showing a card of
          // zeros pointing at pages the worker cannot open (svdy-ui §7).
          <Typography variant="caption" color="text.disabled">
            {t('worker:dashboard.pendingNone')}
          </Typography>
        ) : rows.map((r) => (
          <ClickableRow
            key={r.key}
            label={r.label}
            count={r.count}
            icon={React.cloneElement(NAV_ICON[r.path], { sx: { fontSize: 14 } })}
            // `?status=pending` — the row counts PENDING, so the list it opens must show pending.
            // Without it the destination opened on its own default, which is `all` on the two request
            // lists: a worker clicking 3 landed on everything (owner, production 2026-09-05).
            onClick={() => navigate(`${r.path}?status=pending`)}
          />
        ))}
      </Stack>
    </Paper>
  );
};

const ClickableRow: React.FC<{
  label: string; count: number; icon: React.ReactNode; onClick: () => void;
}> = ({ label, count, icon, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      cursor: 'pointer', py: 0.5, px: 0.5, borderRadius: 0.5,
      '&:hover': { bgcolor: 'grey.50' },
    }}
  >
    <Stack direction="row" alignItems="center" spacing={0.75}>
      {icon}
      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>{label}</Typography>
    </Stack>
    <Chip
      label={count}
      size="small"
      color={count > 0 ? 'warning' : 'default'}
      sx={{ minWidth: 28, height: 20, fontSize: '0.6875rem' }}
    />
  </Box>
);

const MonthCard: React.FC<{ data: DashboardResponse | null; navigate: (to: string) => void }> = ({ data, navigate }) => {
  const { t } = useTranslation();
  const m = data?.month_summary;
  return (
    <Paper
      elevation={0}
      onClick={() => navigate('/me/schedule')}
      sx={{
        ...cardSx,
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        transition: 'border-color 120ms',
        '&:hover': { borderColor: 'primary.main' },
      }}
    >
      <CardHeader
        icon={<CalendarMonthOutlined sx={{ fontSize: 18, color: 'primary.main' }} />}
        title={
          m?.year_month
            ? new Date(m.year_month + '-01').toLocaleDateString(activeLocale(), { year: 'numeric', month: 'long' })
            : t('worker:dashboard.thisMonth')
        }
        action={
          <Typography variant="caption" sx={{ color: 'primary.main', fontSize: '0.72rem', fontWeight: 600 }}>
            {t('worker:dashboard.viewAll')} →
          </Typography>
        }
      />
      {/* CSS Grid (svdy-ui §11.1). Top-aligned, not vertically centred: the card
          beside this one (Leave) starts its first row directly under the header,
          and centring these four pushed them below that line so the two cards in
          the row had different first-content baselines (svdy-ui §11.3). */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
        gap: 1.5,
        alignContent: 'start',
      }}>
        <Stat label={t('worker:dashboard.labelCompleted')} value={m?.completed_days ?? 0} unit="d" tone="success.main" />
        <Stat label={t('worker:dashboard.labelLate')} value={m?.late_days ?? 0} unit="d" tone={m && m.late_days > 0 ? 'warning.main' : 'text.primary'} />
        <Stat label={t('worker:dashboard.labelAbsent')} value={m?.absent_days ?? 0} unit="d" tone={m && m.absent_days > 0 ? 'error.main' : 'text.primary'} />
        <Stat label={t('worker:dashboard.labelOvertime')} value={m?.overtime_hours ?? 0} unit="h" tone="primary.main" />
      </Box>
    </Paper>
  );
};

/**
 * One metric inside the month card.
 *
 * Left-aligned, like every other value on this dashboard (`This week`'s hours,
 * `Leave`'s balance, the punch times). Centring these four made them the only
 * content on the page not anchored to the card's inner edge, so the row read as
 * two cards built to different rules — the month card centred, the leave card
 * beside it left-aligned (owner review 2026-07-29).
 */
const Stat: React.FC<{ label: string; value: number; unit: string; tone: string }> = ({ label, value, unit, tone }) => (
  <Box sx={{ py: 0.5 }}>
    <Typography variant="h5" sx={{ fontWeight: 700, color: tone, lineHeight: 1 }}>
      {Number(value).toFixed(unit === 'h' ? 1 : 0)}{unit}
    </Typography>
    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6875rem' }}>
      {label}
    </Typography>
  </Box>
);

const LeaveCard: React.FC<{
  data: DashboardResponse | null; navigate: (to: string) => void;
}> = ({ data, navigate }) => {
  const { t } = useTranslation();
  return (
  <Paper elevation={0} sx={cardSx}>
    <CardHeader
      icon={<BeachAccessOutlined sx={{ fontSize: 18, color: 'success.main' }} />}
      title={t('worker:dashboard.leave')}
      action={<Button size="small" onClick={() => navigate('/me/leave')} sx={{ textTransform: 'none', fontSize: '0.75rem' }}>
        {t('worker:dashboard.viewAll')}
      </Button>}
    />
    {(data?.leave_balances ?? []).length === 0 ? (
      <CardEmptyState text={t('worker:dashboard.noLeaveBalance')} />
    ) : (
      <Stack spacing={1}>
        {(data?.leave_balances ?? []).slice(0, 3).map((b) => (
          <Box key={b.leave_type_id}>
            <Stack direction="row" justifyContent="space-between" alignItems="baseline">
              <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>{b.leave_type_name}</Typography>
              <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                <strong>{b.remaining}</strong> / {b.total}d
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={b.total > 0 ? (b.used / b.total) * 100 : 0}
              sx={{ height: 3, borderRadius: 2, bgcolor: 'grey.100', mt: 0.25 }}
            />
          </Box>
        ))}
      </Stack>
    )}
  </Paper>
  );
};

const FeedCard: React.FC<{
  title: string; icon: React.ReactNode;
  items: Array<{ id: string; title: string; subtitle: string }>;
  emptyText: string;
  onItemClick?: (id: string) => void;
}> = ({ title, icon, items, emptyText, onItemClick }) => (
  <Paper elevation={0} sx={listCardSx}>
    <CardHeader icon={icon} title={title} />
    {items.length === 0 ? (
      <CardEmptyState text={emptyText} />
    ) : (
      <Stack spacing={1} divider={<Divider />}>
        {items.map((it) => (
          <Box
            key={it.id}
            onClick={onItemClick ? () => onItemClick(it.id) : undefined}
            sx={onItemClick ? {
              cursor: 'pointer',
              mx: -1,
              px: 1,
              borderRadius: 1,
              transition: 'background-color 120ms',
              '&:hover': { bgcolor: 'action.hover' },
            } : undefined}
          >
            <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 500 }}>{it.title}</Typography>
            {it.subtitle && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6875rem' }}>
                {it.subtitle}
              </Typography>
            )}
          </Box>
        ))}
      </Stack>
    )}
  </Paper>
);

export default WorkerDashboard;
